import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useState } from 'react';

interface ModernPaginationProps {
  totalItems: number;
  currentPage?: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

const navButtonClass =
  'inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[hsl(var(--app-border-subtle))] bg-white text-[hsl(var(--app-text-muted))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] hover:text-[hsl(var(--app-text-strong))] disabled:cursor-not-allowed disabled:opacity-40';

export const ModernPagination = ({
  totalItems,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
}: ModernPaginationProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);

  const handleItemsPerPageChange = (value: number) => {
    onItemsPerPageChange(value);
    onPageChange(1);
    setIsDropdownOpen(false);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-[13px] text-[hsl(var(--app-text-muted))]">
        <span>Lignes</span>
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen((open) => !open)}
            className="inline-flex h-8 min-w-[62px] items-center justify-between gap-2 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-white px-3 text-[13px] font-medium text-[hsl(var(--app-text-strong))]"
          >
            {itemsPerPage}
            <ChevronDownIcon />
          </button>
          {isDropdownOpen && (
            <div className="absolute top-full left-0 z-50 mt-1 min-w-full overflow-hidden rounded-xl border border-[hsl(var(--app-border-subtle))] bg-white shadow-[0_16px_30px_rgba(15,23,42,0.08)]">
              {[10, 25, 50, 100].map((value) => (
                <button
                  key={value}
                  onClick={() => handleItemsPerPageChange(value)}
                  className="w-full px-3 py-2 text-left text-[13px] text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]"
                >
                  {value}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-[13px] text-[hsl(var(--app-text-muted))]">
        Page <span className="font-medium text-[hsl(var(--app-text-strong))]">{validCurrentPage}</span> sur{' '}
        <span className="font-medium text-[hsl(var(--app-text-strong))]">{totalPages}</span>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(1)}
          disabled={validCurrentPage <= 1}
          className={navButtonClass}
        >
          <ChevronsLeft className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onPageChange(validCurrentPage - 1)}
          disabled={validCurrentPage <= 1}
          className={navButtonClass}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onPageChange(validCurrentPage + 1)}
          disabled={validCurrentPage >= totalPages}
          className={navButtonClass}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={validCurrentPage >= totalPages}
          className={navButtonClass}
        >
          <ChevronsRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

const ChevronDownIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-[hsl(var(--app-text-muted))]"
  >
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
